// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Ref,
} from 'react';
import { formatMonthYear, maskMonthYear, parseMonthYear } from './monthYearFormat';

/**
 * Props to spread onto a `<TextInput>` or a raw `<input>` so it renders a
 * date value masked as "MM/YYYY" while still emitting a full ISO
 * `string | undefined` upward.
 */
export interface MonthYearRegister {
  ref: Ref<HTMLInputElement>;
  value: string;
  inputMode: 'numeric';
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Controlled-input glue for month/year-only date fields (e.g. "Date
 * employment started"). Mirrors `useGroupedAmount`'s shape: the input shows a
 * "MM/YYYY" mask while the user types, the caret is kept in place across the
 * `/` re-insertion, and `onChange` receives a full ISO date (day defaulted to
 * "01") once both segments are complete — or `undefined` while
 * incomplete/blank/invalid.
 */
export function useMonthYearInput(
  value: unknown,
  onChange: (next: string | undefined) => void,
): MonthYearRegister {
  const ref = useRef<HTMLInputElement>(null);
  const focused = useRef(false);
  const pendingCaret = useRef<number | null>(null);
  const [display, setDisplay] = useState<string>(() => formatMonthYear(value));

  // Reflect external value changes (prefill, programmatic edits) into the
  // display — but never while the user is mid-edit, or an in-progress entry
  // like "07/" would be reformatted out from under them.
  useLayoutEffect(() => {
    if (focused.current) return;
    const next = formatMonthYear(value);
    setDisplay((prev) => (prev === next ? prev : next));
  }, [value]);

  // After a masked re-render the inserted/removed "/" shifts offsets, so put
  // the caret back where the user's content left it.
  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const pos = pendingCaret.current;
      ref.current.setSelectionRange(pos, pos);
    }
    pendingCaret.current = null;
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    // How many digits sit left of the caret; the caret is restored after the
    // same count once the "/" is re-inserted.
    const digitsLeft = (raw.slice(0, caret).match(/\d/g) ?? []).length;
    const masked = maskMonthYear(raw);

    let seen = 0;
    let nextCaret = masked.length;
    for (let i = 0; i < masked.length; i += 1) {
      if (seen >= digitsLeft) {
        nextCaret = i;
        break;
      }
      if (/\d/.test(masked[i])) seen += 1;
    }
    pendingCaret.current = nextCaret;

    setDisplay(masked);
    onChange(parseMonthYear(masked));
  };

  return {
    ref,
    value: display,
    inputMode: 'numeric',
    onChange: handleChange,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: () => {
      focused.current = false;
      // Normalise on blur (drops an incomplete entry, re-masks from the stored value).
      setDisplay(formatMonthYear(value));
    },
  };
}
